import React, {useState} from "react";
import {
    Box, Button, IconButton, Link, MenuItem, Paper, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography
} from "@mui/material";
import {Delete, InsertDriveFile} from "@mui/icons-material";
import S3FileUpload from "../shared/S3FileUpload.js";
import Constants from "../../../../utils/constants.js";
import {toastHelper} from "../../../../utils/toast-helper.js";
import {useCreateFichierMutation, useDeleteFichierMutation} from "../../../../modules/modesOperatoires/modesOperatoires.api.js";

/**
 * Gestion des fichiers PDF (1..N) d'un mode opératoire existant. Chaque fichier
 * est une ressource autonome (add/remove live contre l'API). Disponible en édition.
 */
export default function ModeOperatoireFichiersField({modeOperatoireId, modeOperatoireIri, fichiers = []}) {
    const [createFichier, {isLoading: creating}] = useCreateFichierMutation();
    const [deleteFichier] = useDeleteFichierMutation();

    const [libelle, setLibelle] = useState("");
    const [langue, setLangue] = useState("fr");
    const [url, setUrl] = useState(null);

    const reset = () => {
        setLibelle("");
        setLangue("fr");
        setUrl(null);
    };

    const handleAdd = async () => {
        if (!url || !modeOperatoireIri) return;
        try {
            await createFichier({
                modeOperatoireId,
                // IRI réel (@id, préfixé /api) — un chemin construit à la main est rejeté (« Invalid IRI »)
                modeOperatoire: modeOperatoireIri,
                libelle: libelle || "Document",
                path: url,
                version: 1,
                langue,
            }).unwrap();
            toastHelper.displaySuccess("Fichier ajouté.");
            reset();
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue lors de l'ajout du fichier.");
        }
    };

    const handleDelete = async (f) => {
        try {
            await deleteFichier({id: f.id, modeOperatoireId}).unwrap();
            toastHelper.displaySuccess("Fichier supprimé.");
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue lors de la suppression.");
        }
    };

    return (
        <Box className={"sectionContainer"} sx={{mt: 3}}>
            <Typography variant={"h5"} className={"sectionHeader"}>Fichiers (PDF)</Typography>

            <TableContainer component={Paper} className={"mt-2"}>
                <Table size="small">
                    <TableHead>
                        <TableRow className={"tableHeader-row"}>
                            <TableCell>Libellé</TableCell>
                            <TableCell>Langue</TableCell>
                            <TableCell>Version</TableCell>
                            <TableCell>Fichier</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fichiers.length === 0 &&
                            <TableRow><TableCell colSpan={5} align="center">Aucun fichier</TableCell></TableRow>
                        }
                        {fichiers.map((f) => (
                            <TableRow key={f.id} hover>
                                <TableCell>{f.libelle}</TableCell>
                                <TableCell>{(f.langue || "").toUpperCase()}</TableCell>
                                <TableCell>{f.version}</TableCell>
                                <TableCell>
                                    <Link href={`${Constants.API_ASSETS_DOCUMENT}/${f.path}`} target="_blank" rel="noopener" sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                        <InsertDriveFile fontSize="small"/> Ouvrir
                                    </Link>
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton color="error" onClick={() => handleDelete(f)}>
                                        <Delete/>
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction={{xs: "column", md: "row"}} spacing={2} alignItems={{md: "center"}} sx={{mt: 2}}>
                <TextField
                    label="Libellé du fichier"
                    value={libelle}
                    onChange={(e) => setLibelle(e.target.value)}
                    size="small"
                />
                <TextField
                    label="Langue"
                    value={langue}
                    onChange={(e) => setLangue(e.target.value)}
                    size="small"
                    select
                    sx={{width: 120}}
                >
                    <MenuItem value="fr">FR</MenuItem>
                    <MenuItem value="ar">AR</MenuItem>
                </TextField>
                <S3FileUpload
                    label=""
                    folder="modes_operatoires"
                    accept="application/pdf"
                    value={url}
                    onChange={(u, name) => {
                        setUrl(u);
                        if (u && !libelle && name) setLibelle(name);
                    }}
                />
                <Button variant="contained" onClick={handleAdd} disabled={!url || creating}>
                    Ajouter le fichier
                </Button>
            </Stack>
        </Box>
    );
}
